import { TextField } from "@mui/material";
import Chip from "@mui/material/Chip";
import Typography, { TypographyProps } from "@mui/material/Typography";
import styled from "styled-components";
import { alpha } from "@mui/material";

export const Title = styled(Typography)<
  TypographyProps<"h2", { component: "h2" }>
>`
  margin-right: ${({ theme }) => theme.spacing(1)};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const DetailsTitle = styled.span`
  color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? theme.palette.primary.main
      : theme.palette.text.secondary};
  font-weight: 500;
  font-size: 0.9rem;
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

export const DetailsInfo = styled.span`
  margin-right: ${({ theme }) => theme.spacing(2)};
  font-size: 0.9rem;
`;

export const DetailsInfoColored = styled(DetailsInfo)`
  color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? theme.palette.text.primary
      : theme.palette.primary.main};
  font-weight: 450;
`;

export const DetailsInfoColoredDark = styled(DetailsInfo)`
  color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? theme.palette.text.primary
      : theme.palette.primary.dark};
  font-weight: 410;
`;

export const DetailsInfoColoredSuccess = styled(DetailsInfo)`
  color: ${({ theme }) => theme.palette.success.main};
  font-weight: 450;
`;

export const DetailsInfoColoredWarning = styled(DetailsInfo)`
  color: ${({ theme }) => theme.palette.warning.main};
  font-weight: 450;
`;

export const DetailsInfoColoredDanger = styled(DetailsInfo)`
  color: ${({ theme }) => theme.palette.error.main};
  font-weight: 450;
`;

export const DetailsInfoGray = styled(DetailsInfo)`
  color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? theme.palette.text.primary
      : theme.palette.text.secondary};
  font-weight: 450;
`;

/* separates the bucket summary from the file table below it */
export const SummaryDetails = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => theme.spacing(1, 0, 0, 0)};
`;

/*
  All summary tags share the size, the shape and the typography so the rows stay
  aligned; only the colors tell the states apart. Spacing between neighbouring
  tags comes from the flex gap of their group, not from the tags themselves.
*/
const HeaderTag = styled(Chip)`
  height: 24px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 500;

  & .MuiChip-label {
    padding: ${({ theme }) => theme.spacing(0, 1)};
  }
`;

export const TagGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
`;

export const TagInfo = styled(HeaderTag)`
  border-color: ${({ theme }) => alpha(theme.palette.secondary.main, 0.2)};
  background-color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? theme.palette.grey[900]
      : // : alpha(theme.palette.secondary.main, 0.1)};
        theme.palette.grey[100]};
`;

export const TagDisabled = styled(HeaderTag)`
  border-color: ${({ theme }) => alpha(theme.palette.text.secondary, 0.15)};
  background-color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? alpha(theme.palette.text.secondary, 0.08) //theme.palette.grey[900]
      : alpha(theme.palette.text.secondary, 0.08)};
  /* faded towards the tag background in both themes so the tag reads as muted */
  color: ${({ theme }) => alpha(theme.palette.text.secondary, 0.5)};
`;

export const TagWarning = styled(HeaderTag)`
  border-color: ${({ theme }) => alpha(theme.palette.warning.main, 0.45)};
  background-color: ${({ theme }) => alpha(theme.palette.warning.main, 0.14)};
  /* the plain warning.main is too light on white and too dark on black */
  color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? theme.palette.warning.light
      : theme.palette.warning.dark};
`;

/*
  Both visibility tags keep the neutral TagInfo shell; only the label color tells
  the states apart - public is world readable and reads as a warning, private is
  the safe default and reads as success.
*/
export const TagPublic = styled(TagInfo)`
  color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? theme.palette.secondary.main
      : theme.palette.secondary.dark};
`;

export const ProSearchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
`;

export const ProSearchTextField = styled(TextField)<{ $expanded?: boolean }>`
  width: ${({ $expanded }) => ($expanded ? "200px" : "52px")};
  /* overflow: hidden; */
  /* opacity: ${({ $expanded }) => ($expanded ? "1" : "0")}; */
  transition:
    width 0.3s ease,
    opacity 0.3s ease;
`;

export const PolicyList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
`;

const PolicyChipBase = styled(HeaderTag)`
  & .MuiChip-icon {
    margin-left: ${({ theme }) => theme.spacing(0.75)};
    margin-right: 0;
    color: inherit;
    font-size: 0.9rem;
  }

  & .MuiChip-label {
    padding: ${({ theme }) => theme.spacing(0, 1, 0, 0.75)};
  }
`;

export const PolicyChip = styled(PolicyChipBase)`
  border-color: ${({ theme }) => alpha(theme.palette.secondary.main, 0.35)};
  background-color: ${({ theme }) => alpha(theme.palette.secondary.main, 0.1)};
  color: ${({ theme }) => theme.palette.secondary.main};
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.palette.secondary.main};
    background-color: ${({ theme }) =>
      alpha(theme.palette.secondary.main, 0.22)};
  }
`;

/*
  A policy the bucket does not have yet. Not rendered at the moment: only the
  configured policies are listed. Kept ready for when creating and assigning
  policies is supported, so it has the same shape as the configured chip and is
  meant to be used with an AddIcon.
*/
export const PolicyChipEmpty = styled(PolicyChipBase)`
  /* solid grey badge, no outline: the border stays transparent from HeaderTag */
  background-color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.16)
      : theme.palette.grey[500]};
  color: ${({ theme }) => theme.palette.common.white};
`;
